"use client"

import { type FormEvent, useState } from "react"
import Link from "next/link"
import { primaryButtonClass } from "@/app/components/styles"

type FieldName = "name" | "contact" | "message" | "consent"

const emptyValues = {
  name: "",
  contact: "",
  message: "",
  consent: false,
}

export function ContactForm() {
  const [values, setValues] = useState(emptyValues)
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({})
  const [sent, setSent] = useState(false)

  function update(partial: Partial<typeof emptyValues>) {
    setValues((current) => ({ ...current, ...partial }))
    const key = Object.keys(partial)[0] as FieldName
    setErrors((current) => ({ ...current, [key]: undefined }))
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const nextErrors: Partial<Record<FieldName, string>> = {}

    if (values.name.trim().length < 2) nextErrors.name = "Укажите имя"
    if (values.contact.trim().length < 3) {
      nextErrors.contact = "Укажите почту, телефон или мессенджер"
    }
    if (values.message.trim().length < 10) {
      nextErrors.message = "Опишите задачу хотя бы в паре предложений"
    }
    if (!values.consent) nextErrors.consent = "Нужно согласие на обработку данных"

    setErrors(nextErrors)
    if (Object.keys(nextErrors).length === 0) setSent(true)
  }

  if (sent) {
    return (
      <div className="rounded-[28px] bg-surface px-6 py-10" role="status">
        <h3 className="text-2xl font-semibold tracking-tight">Заявка принята</h3>
        <p className="mt-3 max-w-md text-muted">
          Спасибо, {values.name.trim()}. Свяжемся с вами в ближайший рабочий день.
        </p>
      </div>
    )
  }

  return (
    <form className="space-y-4" onSubmit={onSubmit} noValidate>
      <Field
        label="Ваше имя"
        name="name"
        value={values.name}
        error={errors.name}
        onChange={(name) => update({ name })}
      />
      <Field
        label="Почта, телефон или мессенджер"
        name="contact"
        value={values.contact}
        error={errors.contact}
        onChange={(contact) => update({ contact })}
      />
      <Field
        label="Коротко опишите ваш проект"
        name="message"
        value={values.message}
        error={errors.message}
        multiline
        onChange={(message) => update({ message })}
      />
      <div>
        <label className="flex items-start gap-3 text-sm leading-relaxed text-muted">
          <input
            type="checkbox"
            name="consent"
            checked={values.consent}
            className="mt-1 size-4 accent-accent"
            onChange={(event) => update({ consent: event.target.checked })}
          />
          <span>
            Я согласен на обработку моих{" "}
            <Link href="/privacy" className="text-accent underline-offset-2 hover:underline">
              персональных данных
            </Link>
          </span>
        </label>
        {errors.consent ? <p className="mt-2 text-sm text-red-600">{errors.consent}</p> : null}
      </div>
      <button type="submit" className={primaryButtonClass}>
        Отправить
      </button>
    </form>
  )
}

type FieldProps = {
  label: string
  name: string
  value: string
  error?: string
  multiline?: boolean
  onChange: (value: string) => void
}

function Field({ label, name, value, error, multiline = false, onChange }: FieldProps) {
  const className =
    "w-full rounded-2xl border border-line bg-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-muted/70 focus:border-accent"

  return (
    <label className="block">
      <span className="sr-only">{label}</span>
      {multiline ? (
        <textarea
          name={name}
          rows={4}
          placeholder={label}
          value={value}
          aria-invalid={Boolean(error)}
          className={className}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          name={name}
          placeholder={label}
          value={value}
          aria-invalid={Boolean(error)}
          className={className}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      {error ? <span className="mt-1.5 block text-sm text-red-600">{error}</span> : null}
    </label>
  )
}
